import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Copy, Check } from "lucide-react";

const W = 1200, H = 630;
const field = "w-full bg-black/50 border border-white/10 text-white px-3 py-2 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none";

export const PromoBannerDialog = ({ code, onClose }) => {
  const canvasRef = useRef(null);
  const [headline, setHeadline] = useState("OFFRE SPÉCIALE FORUM");
  const [gains, setGains] = useState("Stage 1 · jusqu'à +30% de couple");
  const [site, setSite] = useState(typeof window !== "undefined" ? window.location.host : "imperium-files.com");
  const [copied, setCopied] = useState(false);

  const reward = code
    ? (code.reward_type === "discount" ? `-${code.discount_pct}% SUR VOS CRÉDITS` : `${code.credits} CRÉDITS OFFERTS`)
    : "";

  const draw = () => {
    const cv = canvasRef.current;
    if (!cv || !code) return;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, W, H);

    // Background gradient
    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, "#070a10");
    bg.addColorStop(0.55, "#0b1220");
    bg.addColorStop(1, "#0a0f1a");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Cyan glow blob
    const glow = ctx.createRadialGradient(W - 220, 160, 40, W - 220, 160, 460);
    glow.addColorStop(0, "rgba(0,229,255,0.22)");
    glow.addColorStop(1, "rgba(0,229,255,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);
    const glow2 = ctx.createRadialGradient(120, H - 80, 20, 120, H - 80, 380);
    glow2.addColorStop(0, "rgba(139,92,246,0.18)");
    glow2.addColorStop(1, "rgba(139,92,246,0)");
    ctx.fillStyle = glow2;
    ctx.fillRect(0, 0, W, H);

    // Top accent stripe
    const stripe = ctx.createLinearGradient(0, 0, W, 0);
    stripe.addColorStop(0, "#00E5FF");
    stripe.addColorStop(0.5, "#8B5CF6");
    stripe.addColorStop(1, "#FF3C6A");
    ctx.fillStyle = stripe;
    ctx.fillRect(0, 0, W, 8);

    // Border frame
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = 2;
    ctx.strokeRect(24, 24, W - 48, H - 48);

    // Brand
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#ffffff";
    ctx.font = "800 40px 'Arial Black', Arial, sans-serif";
    ctx.fillText("IMPERIUM", 70, 110);
    ctx.fillStyle = "#00E5FF";
    ctx.font = "700 15px Arial";
    ctx.fillText("A U T O M O T I V E   F I L E S", 72, 138);

    // Headline
    ctx.fillStyle = "#8B5CF6";
    ctx.font = "700 22px Arial";
    ctx.fillText((headline || "").toUpperCase(), 70, 220);

    // Reward big
    ctx.fillStyle = "#ffffff";
    ctx.font = "800 66px 'Arial Black', Arial, sans-serif";
    wrapText(ctx, reward, 70, 300, W - 140, 68);

    // Gains
    ctx.fillStyle = "#A8B3C4";
    ctx.font = "400 24px Arial";
    ctx.fillText(gains || "", 70, 400);

    // Code box
    const codeText = code.code;
    ctx.font = "800 46px 'Courier New', monospace";
    const cw = ctx.measureText(codeText).width;
    const boxW = cw + 70, boxX = 70, boxY = 452, boxH = 82;
    ctx.fillStyle = "rgba(0,229,255,0.10)";
    roundRect(ctx, boxX, boxY, boxW, boxH, 10);
    ctx.fill();
    ctx.strokeStyle = "#00E5FF";
    ctx.lineWidth = 2;
    roundRect(ctx, boxX, boxY, boxW, boxH, 10);
    ctx.stroke();
    ctx.fillStyle = "#9CA3AF";
    ctx.font = "700 14px Arial";
    ctx.fillText("CODE", boxX + 34, boxY + 26);
    ctx.fillStyle = "#00E5FF";
    ctx.font = "800 46px 'Courier New', monospace";
    ctx.fillText(codeText, boxX + 34, boxY + 66);

    // CTA + site
    ctx.fillStyle = "#ffffff";
    ctx.font = "700 20px Arial";
    ctx.fillText("À valider dans « Crédits » →", boxX + boxW + 40, boxY + 42);
    ctx.fillStyle = "#6B7280";
    ctx.font = "700 18px Arial";
    ctx.textAlign = "right";
    ctx.fillText((site || "").toLowerCase(), W - 70, H - 60);
    ctx.textAlign = "left";
  };

  useEffect(() => {
    if (!code) return undefined;
    let raf;
    const tryDraw = () => {
      if (canvasRef.current) draw();
      else raf = requestAnimationFrame(tryDraw);
    };
    tryDraw();
    return () => { if (raf) cancelAnimationFrame(raf); };
    /* eslint-disable-next-line */
  }, [code, headline, gains, site]);

  const download = () => {
    const cv = canvasRef.current;
    const a = document.createElement("a");
    a.download = `IMPERIUM_promo_${code.code}.png`;
    a.href = cv.toDataURL("image/png");
    a.click();
    toast.success("Bannière téléchargée");
  };

  const copyImage = async () => {
    try {
      const cv = canvasRef.current;
      const blob = await new Promise((res) => cv.toBlob(res, "image/png"));
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      setCopied(true);
      toast.success("Image copiée — collez-la sur le forum");
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error("Copie non supportée — utilisez Télécharger");
    }
  };

  return (
    <Dialog open={!!code} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="popover-solid border-white/10 max-w-3xl" data-testid="promo-banner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl uppercase tracking-tight">Bannière forum — {code?.code}</DialogTitle>
          <DialogDescription className="font-mono text-[11px] text-zinc-500">
            Personnalisez puis téléchargez ou copiez l'image prête à coller sur les forums.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-sm overflow-hidden border border-white/10 bg-black">
          <canvas ref={canvasRef} width={W} height={H} data-testid="promo-banner-canvas" className="w-full h-auto block" />
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mt-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Accroche</label>
            <input data-testid="banner-headline" className={`${field} mt-1`} value={headline} onChange={(e) => setHeadline(e.target.value)} maxLength={40} />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Gains typiques</label>
            <input data-testid="banner-gains" className={`${field} mt-1`} value={gains} onChange={(e) => setGains(e.target.value)} maxLength={60} />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Site (pied)</label>
            <input data-testid="banner-site" className={`${field} mt-1`} value={site} onChange={(e) => setSite(e.target.value)} maxLength={40} />
          </div>
        </div>

        <div className="flex items-center gap-3 mt-4">
          <button data-testid="banner-download-btn" onClick={download}
            className="flex items-center gap-2 px-5 py-2.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm hover:bg-cyan-300 transition-colors">
            <Download className="w-4 h-4" /> Télécharger PNG
          </button>
          <button data-testid="banner-copy-btn" onClick={copyImage}
            className="flex items-center gap-2 px-5 py-2.5 border border-white/20 text-white font-display font-bold uppercase tracking-wider rounded-sm hover:bg-white/5 transition-colors">
            {copied ? <Check className="w-4 h-4 text-signal-success" /> : <Copy className="w-4 h-4" />} Copier l'image
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = (text || "").split(" ");
  let line = "";
  let cy = y;
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, cy);
      line = w;
      cy += lineHeight;
    } else {
      line = test;
    }
  }
  ctx.fillText(line, x, cy);
}
